import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ALL_FORM_OFFICES, SIDEBAR_SITE_ALL_LABEL, getDefaultSidebarSiteUrl, sanitizeSelectedSiteUrlForRole, toCanonicalSidebarSiteUrl } from "@/src/constants/accessControl";
import type { AdmissionOfficeCountry } from "@/src/utils/admissionOffice/resolveAdmissionOfficeRouteFromTask";
import type { FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";
import { resolveCountryAccessFromRole, resolveEffectiveCountryAccess } from "@/src/utils/accessControl/resolveCountryAccessFromRole";

export const SELECTED_SITE_URL_STORAGE_KEY = "selectedSiteUrl";

/**
 * TEMPORARY WITH All: empty / "All" → "" (no site filter).
 * RESTORE WITHOUT All: empty / invalid → first SIDEBAR_SITE_OPTIONS by index (never empty).
 */
const resolveSelectedSiteUrl = (value?: string | null, role?: string | null): string => {
  const next = String(value ?? "").trim();
  const fromRole = resolveCountryAccessFromRole(role);

  // --- TEMPORARY WITH All ---
  const canonical =
    !next || next === SIDEBAR_SITE_ALL_LABEL ? "" : toCanonicalSidebarSiteUrl(next) || getDefaultSidebarSiteUrl();
  return sanitizeSelectedSiteUrlForRole(canonical, fromRole.hasFullCountryAccess, fromRole.allowedOffices);
  // --- end TEMPORARY WITH All ---
};

const readSelectedSiteUrlFromStorage = (role?: string | null): string => {
  const raw = localStorage.getItem(SELECTED_SITE_URL_STORAGE_KEY);
  // Missing key → default All; explicit "" also means All.
  if (raw === null) return resolveSelectedSiteUrl(getDefaultSidebarSiteUrl(), role);
  return resolveSelectedSiteUrl(raw, role);
};

const writeSelectedSiteUrlToStorage = (value: string) => {
  localStorage.setItem(SELECTED_SITE_URL_STORAGE_KEY, value);
};

export interface AccessControlState {
  /** Raw role from auth (`admin`, `ukraine-documents-curator`, …). */
  userRole: string;

  /**
   * Point 1 — site filter (sidebar-office).
   * TEMPORARY WITH All: empty string = All (no site filter).
   * RESTORE WITHOUT All: always a concrete URL from SIDEBAR_SITE_OPTIONS (never empty).
   */
  selectedSiteUrl: string;

  /**
   * Point 2 — reserved for future fine-grained role permissions.
   * Intentionally empty for now.
   */
  rolePermissionsReady: boolean;

  /** Point 3 — country access from role, further locked by selected `office_url`. */
  hasFullCountryAccess: boolean;
  allowedOffices: FormOfficeApi[];
  allowedAdmissionCountries: AdmissionOfficeCountry[];
}

const applyCountryAccess = (
  state: Pick<AccessControlState, "userRole" | "hasFullCountryAccess" | "allowedOffices" | "allowedAdmissionCountries">,
  role: string | null | undefined,
  siteUrl: string,
) => {
  const country = resolveEffectiveCountryAccess(role, siteUrl);
  const nextOffices = country.allowedOffices.length ? country.allowedOffices : ALL_FORM_OFFICES;
  const officesUnchanged =
    state.allowedOffices.length === nextOffices.length &&
    state.allowedOffices.every((office, index) => office === nextOffices[index]);

  if (
    state.userRole === country.userRole &&
    state.hasFullCountryAccess === country.hasFullCountryAccess &&
    officesUnchanged
  ) {
    return;
  }

  state.userRole = country.userRole;
  state.hasFullCountryAccess = country.hasFullCountryAccess;
  state.allowedOffices = nextOffices;
  state.allowedAdmissionCountries = country.allowedAdmissionCountries;
};

const initialFromStorage = (): AccessControlState => {
  const role =
    localStorage.getItem("userRole") ||
    (() => {
      try {
        const raw = localStorage.getItem("userAuthInfo");
        if (!raw) return "";
        const parsed = JSON.parse(raw) as { role?: string };
        return parsed.role ?? "";
      } catch {
        return "";
      }
    })();

  const selectedSiteUrl = readSelectedSiteUrlFromStorage(role);
  writeSelectedSiteUrlToStorage(selectedSiteUrl);
  const country = resolveEffectiveCountryAccess(role, selectedSiteUrl);

  return {
    userRole: country.userRole,
    selectedSiteUrl,
    rolePermissionsReady: false,
    hasFullCountryAccess: country.hasFullCountryAccess,
    allowedOffices: country.allowedOffices.length ? country.allowedOffices : ALL_FORM_OFFICES,
    allowedAdmissionCountries: country.allowedAdmissionCountries,
  };
};

const initialState: AccessControlState = initialFromStorage();

const accessControlSlice = createSlice({
  name: "accessControl",
  initialState,
  reducers: {
    /** Re-read role from storage (page reload / after login writes). */
    hydrateAccessFromStorage(state) {
      const next = initialFromStorage();
      state.userRole = next.userRole;
      state.selectedSiteUrl = next.selectedSiteUrl;
      state.hasFullCountryAccess = next.hasFullCountryAccess;
      state.allowedOffices = next.allowedOffices;
      state.allowedAdmissionCountries = next.allowedAdmissionCountries;
    },

    /** Apply access from a fresh auth payload (login). */
    setAccessFromUser(
      state,
      action: PayloadAction<{ role?: string | null; selectedSiteUrl?: string }>,
    ) {
      if (action.payload.selectedSiteUrl !== undefined) {
        const next = resolveSelectedSiteUrl(action.payload.selectedSiteUrl, action.payload.role);
        state.selectedSiteUrl = next;
        writeSelectedSiteUrlToStorage(next);
      } else {
        const next = resolveSelectedSiteUrl(state.selectedSiteUrl, action.payload.role);
        if (next !== state.selectedSiteUrl) {
          state.selectedSiteUrl = next;
          writeSelectedSiteUrlToStorage(next);
        }
      }
      applyCountryAccess(state, action.payload.role, state.selectedSiteUrl);
    },

    /** Point 1 — sidebar site selector. Persists value (TEMPORARY: All="" allowed). */
    setSelectedSiteUrl(state, action: PayloadAction<string>) {
      const next = resolveSelectedSiteUrl(action.payload, state.userRole);
      state.selectedSiteUrl = next;
      writeSelectedSiteUrlToStorage(next);
      applyCountryAccess(state, state.userRole, next);
    },

    clearAccessControl(state) {
      const defaultSiteUrl = getDefaultSidebarSiteUrl();
      state.selectedSiteUrl = defaultSiteUrl;
      applyCountryAccess(state, "", defaultSiteUrl);
      state.rolePermissionsReady = false;
      writeSelectedSiteUrlToStorage(defaultSiteUrl);
    },
  },
});

export const {
  hydrateAccessFromStorage,
  setAccessFromUser,
  setSelectedSiteUrl,
  clearAccessControl,
} = accessControlSlice.actions;

export default accessControlSlice.reducer;
